import { cloneDeep } from 'lodash';
import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    formatNumericInputValue,
    formatPercentValue,
    formatPricingResultValue,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
} from '@/utils/priceListProductCalculator';
import {
    convertSupplierListPriceAmount,
    PRICING_RESULT_FIELD_KEYS,
} from '@/utils/priceListPricingFieldSync';
import {
    cloneTransportCostBreakdown,
    createDefaultTransportCostBreakdown,
    normalizeTransportCostBreakdown,
    TRANSPORT_COST_MODE_FORMULA,
    TRANSPORT_COST_MODE_PERCENT,
} from '@/utils/transportCostBreakdown';
import {
    cloneStampDutyBreakdown,
    createDefaultStampDutyBreakdown,
    normalizeStampDutyBreakdown,
    STAMP_DUTY_MODE_FORMULA,
    STAMP_DUTY_MODE_MANUAL,
} from '@/utils/stampDutyBreakdown';

/** Fields denominated in the supplier working currency (not RON results). */
const SUPPLIER_CURRENCY_AMOUNT_FIELDS = new Set([
    'supplierListPriceNew',
    'finalEntryPriceExVat',
    'netEntryPriceExVat',
]);

/** @typedef {{ key: string, format?: string }} ResultFieldDef */

/**
 * Default result-field defs for baseline snapshots outside the Vue builder
 * (bulk update / propagate). Must include result keys so Valoare veche works
 * on calculated prices — matching PriceListProductBuilderModal.resultFields.
 * @type {ResultFieldDef[]}
 */
export const DEFAULT_BASELINE_RESULT_FIELDS = PRICING_RESULT_FIELD_KEYS.map(
    (key) => ({ key, format: 'money2' }),
);

/**
 * @param {Record<string, unknown>} tab
 * @param {Record<string, unknown>} rootForm
 * @param {Record<string, unknown>} pricingSettings
 * @param {ResultFieldDef[]} resultFields
 * @returns {Record<string, unknown>}
 */
export function buildPricingFieldBaselineSnapshot(
    tab,
    rootForm,
    pricingSettings,
    resultFields = [],
) {
    const merged = mergeFormWithFurnizorPricing(rootForm, tab);
    const computed = calculateProductPricing(
        builderFormToCalculatorInput(merged),
        pricingSettings,
    );

    /** @type {Record<string, unknown>} */
    const snapshot = {
        supplierListPriceNew: merged.supplierListPriceNew ?? '',
        supplierListPriceOld: merged.supplierListPriceOld ?? '',
        currency: merged.currency ?? 'ron',
        discounts: Array.isArray(merged.discounts) ? [...merged.discounts] : [],
        transportCostRate: merged.transportCostRate ?? '',
        transportCostEstimated: merged.transportCostEstimated ?? '',
        // Keep formula state so reset does not restore % then let formula wipe it
        // to empty (−100% vs Valoare veche).
        transportCostBreakdown: cloneTransportCostBreakdown(
            merged.transportCostBreakdown,
        ),
        stampDutyAmount: merged.stampDutyAmount ?? '0',
        stampDutyCurrency: merged.stampDutyCurrency ?? 'ron',
        stampDutyBreakdown: cloneStampDutyBreakdown(merged.stampDutyBreakdown),
        markupReference: merged.markupReference ?? '',
        markupActual: merged.markupActual ?? '',
        // Non-pricing furnizor fields used by propagate-to-peers detection.
        paymentTermDays: merged.paymentTermDays ?? '',
        notes: merged.notes ?? '',
        overrideListPriceRonIncVatFinal: Boolean(
            merged.overrideListPriceRonIncVatFinal,
        ),
        overrideListPriceRonIncVatFinalValue:
            merged.overrideListPriceRonIncVatFinalValue ?? '',
        useManualFinalPrice: Boolean(merged.useManualFinalPrice),
        manualListPriceRonIncVatFinal: merged.manualListPriceRonIncVatFinal ?? '',
        priceAdjustmentRatio: merged.priceAdjustmentRatio ?? '',
        listPriceRonIncVatReference: merged.listPriceRonIncVatReference ?? '',
    };

    resultFields.forEach((field) => {
        snapshot[field.key] = formatPricingResultValue(
            field.key,
            computed[field.key],
            field.format,
        );
    });

    return snapshot;
}

/**
 * @param {Record<string, unknown>} rootForm
 * @param {Record<string, unknown>} pricingSettings
 * @param {ResultFieldDef[]} resultFields
 * @returns {Record<string, Record<string, unknown>>}
 */
export function buildAllPricingFieldBaselines(
    rootForm,
    pricingSettings,
    resultFields = [],
) {
    const tabs = Array.isArray(rootForm.furnizorPricings)
        ? rootForm.furnizorPricings
        : [];
    /** @type {Record<string, Record<string, unknown>>} */
    const baselines = {};
    tabs.forEach((tab) => {
        if (!tab?.clientKey) {
            return;
        }
        baselines[tab.clientKey] = buildPricingFieldBaselineSnapshot(
            tab,
            rootForm,
            pricingSettings,
            resultFields,
        );
    });
    return baselines;
}

/**
 * @param {string} fieldKey
 * @param {unknown} current
 * @param {unknown} baseline
 * @returns {boolean}
 */
export function pricingFieldValuesEqual(fieldKey, current, baseline) {
    if (fieldKey === 'currency') {
        return String(current ?? '').trim() === String(baseline ?? '').trim();
    }

    if (fieldKey === 'transportCostBreakdown') {
        return (
            JSON.stringify(normalizeTransportCostBreakdown(current))
            === JSON.stringify(normalizeTransportCostBreakdown(baseline))
        );
    }

    if (fieldKey === 'stampDutyBreakdown') {
        return (
            JSON.stringify(normalizeStampDutyBreakdown(current))
            === JSON.stringify(normalizeStampDutyBreakdown(baseline))
        );
    }

    const numericFields = new Set([
        'supplierListPriceNew',
        'transportCostRate',
        'transportCostEstimated',
        'stampDutyAmount',
        'markupReference',
        'markupActual',
        'overrideListPriceRonIncVatFinalValue',
        'manualListPriceRonIncVatFinal',
        'priceAdjustmentRatio',
        'listPriceRonIncVatReference',
        ...PRICING_RESULT_FIELD_KEYS,
    ]);

    if (numericFields.has(fieldKey) || fieldKey.startsWith('discounts.')) {
        return parseNumericInput(current) === parseNumericInput(baseline);
    }

    return String(current ?? '').trim() === String(baseline ?? '').trim();
}

/**
 * @param {string} fieldKey
 * @param {Record<string, unknown>|undefined} baseline
 * @param {Record<string, unknown>} form
 * @param {(fieldKey: string, format?: string) => string} formatResultValue
 * @param {ResultFieldDef[]} resultFields
 * @param {Record<string, unknown>|null} [pricingSettings]
 * @returns {boolean}
 */
export function isPricingFieldChangedFromBaseline(
    fieldKey,
    baseline,
    form,
    formatResultValue,
    resultFields = [],
    pricingSettings = null,
) {
    if (!baseline) {
        return false;
    }

    if (fieldKey.startsWith('discounts.')) {
        const index = Number(fieldKey.split('.')[1]);
        const discounts = Array.isArray(form.discounts) ? form.discounts : [];
        return !pricingFieldValuesEqual(
            fieldKey,
            discounts[index],
            baseline.discounts?.[index],
        );
    }

    if (fieldKey === 'currency') {
        return !pricingFieldValuesEqual(fieldKey, form.currency, baseline.currency);
    }

    // Pure currency change: compare FX-converted baseline amounts so converted
    // pret lista / pret intrare final do not orange-highlight as "edited".
    if (
        pricingSettings
        && SUPPLIER_CURRENCY_AMOUNT_FIELDS.has(fieldKey)
        && !pricingFieldValuesEqual('currency', form.currency, baseline.currency)
    ) {
        const convertedBaseline = convertSupplierListPriceAmount(
            baseline[fieldKey],
            baseline.currency,
            form.currency,
            pricingSettings,
        );
        if (PRICING_RESULT_FIELD_KEYS.includes(fieldKey)) {
            const fieldDef = resultFields.find((field) => field.key === fieldKey);
            const current = formatResultValue(fieldKey, fieldDef?.format);
            return !pricingFieldValuesEqual(fieldKey, current, convertedBaseline);
        }
        return !pricingFieldValuesEqual(
            fieldKey,
            form[fieldKey],
            convertedBaseline,
        );
    }

    if (
        fieldKey === 'overrideListPriceRonIncVatFinal'
        || fieldKey === 'useManualFinalPrice'
    ) {
        return Boolean(form[fieldKey]) !== Boolean(baseline[fieldKey]);
    }

    if (fieldKey === 'transportCostBreakdown') {
        // Legacy baselines without breakdown: only rate is compared.
        if (!Object.prototype.hasOwnProperty.call(baseline, 'transportCostBreakdown')) {
            return false;
        }
        return !pricingFieldValuesEqual(
            fieldKey,
            form.transportCostBreakdown,
            baseline.transportCostBreakdown,
        );
    }

    if (fieldKey === 'stampDutyBreakdown') {
        if (!Object.prototype.hasOwnProperty.call(baseline, 'stampDutyBreakdown')) {
            return false;
        }
        return !pricingFieldValuesEqual(
            fieldKey,
            form.stampDutyBreakdown,
            baseline.stampDutyBreakdown,
        );
    }

    if (PRICING_RESULT_FIELD_KEYS.includes(fieldKey)) {
        const fieldDef = resultFields.find((field) => field.key === fieldKey);
        const current = formatResultValue(fieldKey, fieldDef?.format);
        return !pricingFieldValuesEqual(fieldKey, current, baseline[fieldKey]);
    }

    return !pricingFieldValuesEqual(fieldKey, form[fieldKey], baseline[fieldKey]);
}

/**
 * Restores editable pricing inputs from a captured baseline snapshot.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>|null|undefined} baseline
 */
export function restorePricingFieldsFromBaseline(form, baseline) {
    if (!baseline) {
        return;
    }

    form.supplierListPriceNew = baseline.supplierListPriceNew ?? '';
    form.supplierListPriceOld = baseline.supplierListPriceOld
        ?? baseline.supplierListPriceNew
        ?? '';
    form.currency = baseline.currency ?? 'ron';
    form.discounts = Array.isArray(baseline.discounts)
        ? [...baseline.discounts]
        : [];

    // Transport: restore open-session % and drop a calculator formula that was
    // applied after open — empty rate must become "0" (not "" → −100% hint).
    const restoredRate = String(baseline.transportCostRate ?? '').trim() || '0';
    form.transportCostRate = restoredRate;
    if (Object.prototype.hasOwnProperty.call(baseline, 'transportCostBreakdown')) {
        const breakdown = cloneTransportCostBreakdown(
            baseline.transportCostBreakdown,
        );
        if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
            // Baseline had an active formula — restore it, but pin % if formula
            // would recompute empty and wipe the restored rate.
            breakdown.rateIsManual = Boolean(breakdown.rateIsManual);
            form.transportCostBreakdown = breakdown;
            form.transportCostEstimated = baseline.transportCostEstimated ?? '';
            if (!breakdown.rateIsManual) {
                // Keep restored % visible even if lines/goods are incomplete.
                breakdown.rateIsManual = true;
                form.transportCostBreakdown = breakdown;
            }
        } else {
            // Percent baseline: clear calculator application from the builder.
            form.transportCostBreakdown = createDefaultTransportCostBreakdown({
                mode: TRANSPORT_COST_MODE_PERCENT,
                rateIsManual: true,
            });
            form.transportCostEstimated = '';
        }
    } else {
        // Legacy snapshot: exit live formula so it cannot overwrite restored %.
        form.transportCostBreakdown = createDefaultTransportCostBreakdown({
            mode: TRANSPORT_COST_MODE_PERCENT,
            rateIsManual: true,
        });
        form.transportCostEstimated = '';
    }
    form.transportCostRate = restoredRate;

    const restoredStampDuty = String(baseline.stampDutyAmount ?? '').trim() || '0';
    form.stampDutyAmount = restoredStampDuty;
    form.stampDutyCurrency = baseline.stampDutyCurrency ?? 'ron';
    if (Object.prototype.hasOwnProperty.call(baseline, 'stampDutyBreakdown')) {
        const stampBreakdown = cloneStampDutyBreakdown(baseline.stampDutyBreakdown);
        if (stampBreakdown.mode === STAMP_DUTY_MODE_FORMULA) {
            stampBreakdown.amountIsManual = Boolean(stampBreakdown.amountIsManual);
            form.stampDutyBreakdown = stampBreakdown;
            if (!stampBreakdown.amountIsManual) {
                stampBreakdown.amountIsManual = true;
                form.stampDutyBreakdown = stampBreakdown;
            }
        } else {
            form.stampDutyBreakdown = createDefaultStampDutyBreakdown({
                mode: STAMP_DUTY_MODE_MANUAL,
                amountIsManual: true,
            });
        }
    } else {
        form.stampDutyBreakdown = createDefaultStampDutyBreakdown({
            mode: STAMP_DUTY_MODE_MANUAL,
            amountIsManual: true,
        });
    }
    form.stampDutyAmount = restoredStampDuty;

    form.markupReference = baseline.markupReference ?? '';
    form.markupActual = baseline.markupActual ?? '';
    form.overrideListPriceRonIncVatFinal = Boolean(
        baseline.overrideListPriceRonIncVatFinal,
    );
    form.overrideListPriceRonIncVatFinalValue =
        baseline.overrideListPriceRonIncVatFinalValue ?? '';
    form.useManualFinalPrice = Boolean(baseline.useManualFinalPrice);
    form.manualListPriceRonIncVatFinal =
        baseline.manualListPriceRonIncVatFinal ?? '';
    form.priceAdjustmentRatio = baseline.priceAdjustmentRatio ?? '';
    form.listPriceRonIncVatReference = baseline.listPriceRonIncVatReference ?? '';
}

/**
 * @param {string} fieldKey
 * @param {Record<string, unknown>|undefined} baseline
 * @returns {unknown}
 */
export function baselineValueForField(fieldKey, baseline) {
    if (!baseline) {
        return '';
    }

    if (fieldKey.startsWith('discounts.')) {
        const index = Number(fieldKey.split('.')[1]);
        return baseline.discounts?.[index] ?? '';
    }

    return baseline[fieldKey] ?? '';
}

/**
 * @param {unknown} current
 * @param {unknown} baseline
 * @returns {number|null}
 */
export function calculatePricingValueChangeRatio(current, baseline) {
    const baselineNum = parseNumericInput(baseline);
    const currentNum = parseNumericInput(current);
    if (!(baselineNum > 0)) {
        return null;
    }
    if (!Number.isFinite(currentNum)) {
        return null;
    }
    return (currentNum - baselineNum) / baselineNum;
}

/**
 * Change ratio for %-point fields (transport, markup, discounts).
 * When the prior value is 0/empty, uses a percentage-point delta
 * (e.g. 0 → 40 shows as +40%) instead of a relative ratio.
 * When the current value is 0/empty, returns null (no badge) — clearing
 * a % field should not show −100% / −N%.
 *
 * @param {unknown} current
 * @param {unknown} baseline
 * @returns {number|null}
 */
export function calculatePricingPercentFieldChangeRatio(current, baseline) {
    const baselineNum = parseNumericInput(baseline);
    const currentNum = parseNumericInput(current);
    // Cleared to 0%/empty: do not show a change badge.
    if (!Number.isFinite(currentNum) || currentNum === 0) {
        return null;
    }
    if (baselineNum > 0) {
        return calculatePricingValueChangeRatio(current, baseline);
    }
    // 0 → X: percentage-point delta (X means X%).
    return currentNum / 100;
}

/**
 * @param {number|null} ratio
 * @returns {string}
 */
export function pricingFieldChangeHintClass(ratio) {
    if (ratio === null || ratio === 0) {
        return 'text-medium-emphasis';
    }
    // Bigger = red, smaller = green (client preference for price-list diffs).
    return ratio > 0 ? 'text-error' : 'text-success';
}

/**
 * @param {Record<string, unknown>} tab
 * @param {Record<string, unknown>} rootForm
 * @param {Record<string, unknown>} pricingSettings
 * @param {ResultFieldDef[]} resultFields
 * @returns {Record<string, unknown>}
 */
export function buildPricingFieldReferenceSnapshot(
    tab,
    rootForm,
    pricingSettings,
    resultFields = [],
) {
    const merged = mergeFormWithFurnizorPricing(rootForm, tab);
    const oldSupplierPrice = String(merged.supplierListPriceOld ?? '').trim();
    const referenceMerged = oldSupplierPrice
        ? { ...merged, supplierListPriceNew: oldSupplierPrice }
        : merged;
    const computed = calculateProductPricing(
        builderFormToCalculatorInput(referenceMerged),
        pricingSettings,
    );

    /** @type {Record<string, unknown>} */
    const snapshot = {
        discounts: Array.isArray(merged.discounts) ? [...merged.discounts] : [],
        supplierListPriceNew: oldSupplierPrice || merged.supplierListPriceNew || '',
        transportCostRate: merged.transportCostRate ?? '',
        stampDutyAmount: merged.stampDutyAmount ?? '0',
        stampDutyCurrency: merged.stampDutyCurrency ?? 'ron',
        markupReference: merged.markupReference ?? '',
        markupActual: merged.markupActual ?? '',
    };

    resultFields.forEach((field) => {
        if (field.key === 'listPriceRonIncVatFinal') {
            const reference = String(
                referenceMerged.listPriceRonIncVatReference ?? '',
            ).trim();
            const referenceNum = parseNumericInput(reference);
            snapshot[field.key] = referenceNum > 0
                ? formatPricingResultValue(field.key, referenceNum, field.format)
                : formatPricingResultValue(
                    field.key,
                    computed[field.key],
                    field.format,
                );
            return;
        }
        snapshot[field.key] = formatPricingResultValue(
            field.key,
            computed[field.key],
            field.format,
        );
    });

    return snapshot;
}

/**
 * @param {Record<string, unknown>} rootForm
 * @param {Record<string, unknown>} pricingSettings
 * @param {ResultFieldDef[]} resultFields
 * @returns {Record<string, Record<string, unknown>>}
 */
export function buildAllPricingFieldReferenceBaselines(
    rootForm,
    pricingSettings,
    resultFields = [],
) {
    const tabs = Array.isArray(rootForm.furnizorPricings)
        ? rootForm.furnizorPricings
        : [];
    /** @type {Record<string, Record<string, unknown>>} */
    const baselines = {};
    tabs.forEach((tab) => {
        if (!tab?.clientKey) {
            return;
        }
        baselines[tab.clientKey] = buildPricingFieldReferenceSnapshot(
            tab,
            rootForm,
            pricingSettings,
            resultFields,
        );
    });
    return baselines;
}

/**
 * Snapshot pre-edit baselines for draft builderState (bulk / propagate).
 * Includes result fields so Valoare veche works on calculated prices.
 *
 * @param {Record<string, unknown>} preEditForm
 * @param {Record<string, unknown>} pricingSettings
 * @param {ResultFieldDef[]} [resultFields]
 * @returns {{
 *   pricingFieldBaselinesByTab: Record<string, Record<string, unknown>>,
 *   pricingFieldSessionStartBaselinesByTab: Record<string, Record<string, unknown>>,
 *   pricingFieldLastSavedBaselinesByTab: Record<string, Record<string, unknown>>,
 *   pricingFieldReferenceBaselinesByTab: Record<string, Record<string, unknown>>,
 * }}
 */
export function buildDraftPricingBaselinesFromPreEditForm(
    preEditForm,
    pricingSettings,
    resultFields = DEFAULT_BASELINE_RESULT_FIELDS,
) {
    const baselines = buildAllPricingFieldBaselines(
        preEditForm,
        pricingSettings,
        resultFields,
    );
    const referenceBaselines = buildAllPricingFieldReferenceBaselines(
        preEditForm,
        pricingSettings,
        resultFields,
    );
    return {
        pricingFieldBaselinesByTab: cloneDeep(baselines),
        pricingFieldSessionStartBaselinesByTab: cloneDeep(baselines),
        pricingFieldLastSavedBaselinesByTab: cloneDeep(baselines),
        pricingFieldReferenceBaselinesByTab: cloneDeep(referenceBaselines),
    };
}

/**
 * Merge freshly captured pre-edit baselines with an existing draft builderState.
 * Keeps open/reference baselines from the original draft (orange vs first open),
 * advances Valoare veche (lastSaved) to the state before this apply.
 *
 * @param {ReturnType<typeof buildDraftPricingBaselinesFromPreEditForm>} freshBaselines
 * @param {Record<string, unknown>|null|undefined} existingBuilderState
 * @returns {ReturnType<typeof buildDraftPricingBaselinesFromPreEditForm>}
 */
export function mergeDraftPricingBaselinesWithExisting(
    freshBaselines,
    existingBuilderState,
) {
    const existingOpen = existingBuilderState?.pricingFieldBaselinesByTab;
    if (
        !existingOpen
        || typeof existingOpen !== 'object'
        || !Object.keys(existingOpen).length
    ) {
        return freshBaselines;
    }
    const existingSession =
        existingBuilderState?.pricingFieldSessionStartBaselinesByTab;
    const existingReference =
        existingBuilderState?.pricingFieldReferenceBaselinesByTab;
    return {
        pricingFieldBaselinesByTab: cloneDeep(existingOpen),
        pricingFieldSessionStartBaselinesByTab: cloneDeep(
            existingSession && Object.keys(existingSession).length
                ? existingSession
                : existingOpen,
        ),
        // Valoare veche = values before this bulk/propagate apply.
        pricingFieldLastSavedBaselinesByTab: cloneDeep(
            freshBaselines.pricingFieldLastSavedBaselinesByTab,
        ),
        pricingFieldReferenceBaselinesByTab: cloneDeep(
            existingReference && Object.keys(existingReference).length
                ? existingReference
                : freshBaselines.pricingFieldReferenceBaselinesByTab,
        ),
    };
}

/**
 * @param {unknown} savedValue
 * @param {unknown} referenceValue
 * @param {number} [decimals]
 * @returns {string}
 */
export function formatPricingResultSavedHint(
    savedValue,
    referenceValue,
    decimals = 1,
) {
    return formatPricingFieldSavedHint(savedValue, referenceValue, {
        asPercent: false,
        valueDecimals: 2,
        ratioDecimals: decimals,
    });
}

/**
 * @param {unknown} savedValue
 * @param {unknown} referenceValue
 * @param {{
 *   asPercent?: boolean,
 *   valueDecimals?: number,
 *   ratioDecimals?: number,
 *   requirePositiveReference?: boolean,
 * }} [options]
 * @returns {string}
 */
export function formatPricingFieldSavedHint(
    savedValue,
    referenceValue,
    {
        asPercent = false,
        valueDecimals = 2,
        ratioDecimals = 1,
        // Percent fields allow a 0% old value ("Valoare veche: 0%").
        requirePositiveReference,
    } = {},
) {
    const mustRequirePositiveReference = requirePositiveReference ?? !asPercent;
    const savedRaw = String(savedValue ?? '').trim();
    // Empty = no hint. Explicit 0 is shown (e.g. brand-new furnizor Valoare veche).
    if (!savedRaw) {
        return '';
    }
    const savedNum = parseNumericInput(savedValue);
    if (!Number.isFinite(savedNum)) {
        return '';
    }
    if (mustRequirePositiveReference && savedNum < 0) {
        return '';
    }
    const displayText = asPercent
        ? `${formatNumericInputValue(savedNum, valueDecimals)}%`
        : formatNumericInputValue(savedNum, valueDecimals);
    const ratioReference =
        String(referenceValue ?? '').trim() !== ''
            ? referenceValue
            : savedValue;
    const ratio = asPercent
        ? calculatePricingPercentFieldChangeRatio(savedValue, ratioReference)
        : calculatePricingValueChangeRatio(savedValue, ratioReference);
    if (ratio === null || ratio === 0) {
        return `Valoare veche: ${displayText}`;
    }
    return `Valoare veche: ${displayText} (${formatPercentValue(ratio, ratioDecimals)})`;
}
